import React, { useState, useEffect, useContext } from 'react';
import _get from 'lodash/get';
import _has from 'lodash/has';
import _isNil from 'lodash/isNil';
import _isFunction from 'lodash/isFunction';
import _noop from 'lodash/noop';
import _map from 'lodash/map';
import _filter from 'lodash/filter';
import _find from 'lodash/find';
import _isEmpty from 'lodash/isEmpty';
import _fromPairs from 'lodash/fromPairs';

import { CustomFooter } from '../customModal';
import { AppContext, ModalContext } from '../../context';
import { KeyConfig, KeyValueViewProps } from './types';

const useKeyValueView = ({ keyConfig, forceUpdate, turnForceUpdateOff, turnForceUpdateOn }: KeyValueViewProps) => {
	const [keyData, setKeyData] = useState();
	const [keyFilters, setKeyFilters] = useState<Object>([]);
	const [isCollapsed, setIsCollapsed] = useState(_get(keyConfig, 'initiallyCollapsed', false));
	const [fetchOnExpand, setFetchOnExpand] = useState<boolean>(true);
	const [childForceUpdate, setChildForceUpdate] = useState({});
	const [showLoader, setShowLoader] = useState<boolean>(false);
	const { showAlert } = useContext(AppContext);
	const { setModalConfig } = useContext(ModalContext);

	const fetchOnlyOnFilterChange = _get(keyConfig, 'fetchOnlyOnFilterChange', false);
	const isHeavyFetch = _get(keyConfig, 'isHeavyFetch', false);

	const fetchKeyData = async (filters: Object) => {
		setShowLoader(true);
		try {
			const fetchData = _get(keyConfig, 'fetchData');
			if (_isFunction(fetchData)) {
				setKeyData(await fetchData(filters));
				setFetchOnExpand(false);
				setShowLoader(false);
			}
		} catch (error: any) {
			setShowLoader(false);
			showAlert(_get(error, ['response', 'data', 'error'], 'Unable to fetch data'), { type: 'error' });
		}
	};

	const handleForceUpdate = () => {
		if (_isFunction(turnForceUpdateOn)) {
			turnForceUpdateOn();
		} else if (_has(keyConfig, 'fetchData')) {
			fetchKeyData(keyFilters);
		}
	};

	const turnForceUpdateOffForChild = (name: string) => () => {
		setChildForceUpdate((prev) => ({ ...prev, [name]: false }));
	};

	const turnForceUpdateOnForChild = (name: string) => () => {
		const configForName = _find(_get(keyConfig, 'childConfig', []), (config: KeyConfig) => config.name === name);
		if (!configForName) {
			return;
		}
		if (_has(configForName, 'fetchData')) {
			setChildForceUpdate((prev) => ({ ...prev, [name]: true }));
		} else {
			handleForceUpdate();
		}
	};

	useEffect(() => {
		const childConfig = _get(keyConfig, 'childConfig', []);
		if (!_isEmpty(childConfig)) {
			const dependendentChildren = _filter(childConfig, (childKeyConfig) => !_isFunction(_get(childKeyConfig, 'fetchData')));
			setChildForceUpdate(_fromPairs(_map(dependendentChildren, (childKeyConfig) => [_get(childKeyConfig, 'name'), true])));
		}
	}, [keyData]);

	const heavyFetchOnAgreeClick = (filters: any) => {
		fetchKeyData(filters);
		setModalConfig({ isOpen: false });
	};
	const heavyFetchOnDisagreeClick = () => {
		setModalConfig({ isOpen: false });
		setIsCollapsed(true);
	};
	const setHeavyFetchConfirmationModal = (filters: any) => {
		setModalConfig({
			isOpen: true,
			content: 'This will take time, Do you want to continue ?',
			header: 'Data Fetch Confirmation',
			footer: (
				<CustomFooter
					agreeLabel='Confirm'
					disagreeLabel='Back'
					onAgreeClick={() => heavyFetchOnAgreeClick(filters)}
					onDisagreeClick={heavyFetchOnDisagreeClick}
				/>
			)
		});
	};

	const handleFilterChange = (filters: object) => {
		if (fetchOnlyOnFilterChange && isHeavyFetch) {
			setHeavyFetchConfirmationModal(filters);
		} else {
			fetchKeyData(filters);
		}
		setKeyFilters(filters);
	};

	const getChildData = (name: string) => () => {
		return _get(keyData, name);
	};

	// if label is not present then it means that its not a collapsile panel
	// so fetch the data on mounting
	// otherwise fetch the data on expanding
	useEffect(() => {
		if (!fetchOnlyOnFilterChange) {
			if (_isNil(_get(keyConfig, 'label'))) {
				fetchKeyData(keyFilters);
			} else if (!isCollapsed && fetchOnExpand) {
				if (isHeavyFetch) {
					setHeavyFetchConfirmationModal(keyFilters);
				} else {
					fetchKeyData(keyFilters);
				}
			}
		}
	}, [isCollapsed]);

	useEffect(() => {
		if (forceUpdate) {
			fetchKeyData(keyFilters);
			if (_isFunction(turnForceUpdateOff)) {
				turnForceUpdateOff();
			}
		}
	}, [forceUpdate]);

	const copyToClipboard = (event: any) => {
		event.preventDefault();
		event.stopPropagation();

		const format = _get(keyConfig, ['override', 'format']);

		if (format === 'datalist') {
			const data = _get(keyConfig, ['override', 'datalistProps', 'fetchData'], _noop)();
			navigator.clipboard.writeText(JSON.stringify(_isNil(data) ? '' : data, null, 2));
		} else {
			navigator.clipboard.writeText(JSON.stringify(_isNil(keyData) ? '' : keyData, null, 2));
		}
	};

	return {
		handleFilterChange,
		getChildData,
		keyData,
		childForceUpdate,
		turnForceUpdateOffForChild,
		turnForceUpdateOnForChild,
		handleForceUpdate,
		setIsCollapsed,
		isCollapsed,
		showLoader,
		copyToClipboard,
		setKeyData
	};
};

export default useKeyValueView;
